/**
 * @file shipping-region-utils.ts
 *
 * Pure helpers for translating between the WooCommerce region model
 * ({@link ShippingZoneLocation}) and human-readable text, plus the flattened
 * country list the pickup/region selectors need. Resolving a location to its
 * continent/country/state name and the "ships to" summary live here so the
 * overview and the forms share one implementation. Also formats a pickup
 * location's address line.
 */

import type {
  ShippingZoneLocation,
  ShopContinent,
  ShopPickupLocation,
} from '../api/shop-types';
import { ALL_REGIONS, ALL_REGIONS_LOCATION } from './shipping-constants';

/** A `{ code, name }` country flattened out of the continent tree. */
export interface CountryRef {
  code: string;
  name: string;
}

/** A `{ value, label }` option for a native country `<select>`. */
export interface CountryOption {
  value: string;
  label: string;
}

/**
 * Flattens every country out of the continent tree, de-duplicated by code and
 * sorted by name.
 *
 * @param continents - The region tree.
 *
 * @returns The unique countries, sorted alphabetically by name.
 */
export function flattenCountries(continents: ShopContinent[]): CountryRef[] {
  const byCode = new Map<string, CountryRef>();
  for (const continent of continents) {
    for (const country of continent.countries) {
      if (!byCode.has(country.code)) {
        byCode.set(country.code, { code: country.code, name: country.name });
      }
    }
  }
  return [...byCode.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Builds country `<select>` options from the continent tree.
 *
 * @param continents - The region tree.
 *
 * @returns The country options, sorted by label.
 */
export function getCountryOptions(continents: ShopContinent[]): CountryOption[] {
  return flattenCountries(continents).map((country) => ({
    value: country.code,
    label: country.name,
  }));
}

/**
 * Finds a country (across all continents) by its code.
 *
 * @param continents - The region tree.
 * @param code - The country code.
 *
 * @returns The country entry, or `undefined`.
 */
export function findCountry(
  continents: ShopContinent[],
  code: string,
): ShopContinent['countries'][number] | undefined {
  for (const continent of continents) {
    const country = continent.countries.find((c) => c.code === code);
    if (country) {
      return country;
    }
  }
  return undefined;
}

/**
 * Resolves a zone location to its display name, falling back to the raw code when
 * the reference data doesn't contain it.
 *
 * @param location - The zone location.
 * @param continents - The region tree.
 *
 * @returns The continent/country/state name, or the raw code.
 */
export function getShippingRegionLabel(
  location: ShippingZoneLocation,
  continents: ShopContinent[],
): string {
  switch (location.type) {
    case 'continent':
      return continents.find((c) => c.code === location.code)?.name ?? location.code;
    case 'country':
      return findCountry(continents, location.code)?.name ?? location.code;
    case 'state': {
      const [countryCode, stateCode] = location.code.split(':');
      const country = findCountry(continents, countryCode);
      const state = country?.states.find((s) => s.code === stateCode);
      return state?.name ?? location.code;
    }
    default:
      return location.code;
  }
}

/**
 * Whether a zone's locations mean "all regions" — either empty or containing the
 * `all_regions` sentinel.
 *
 * @param locations - The zone's locations.
 *
 * @returns `true` when the zone ships everywhere.
 */
export function isAllRegions(locations: ShippingZoneLocation[]): boolean {
  return (
    locations.length === 0 || locations.some((l) => l.type === ALL_REGIONS)
  );
}

/**
 * Builds the "ships to" summary: the specific region names joined by commas
 * (excluding the `all_regions` sentinel).
 *
 * @param locations - The zone's locations.
 * @param continents - The region tree.
 *
 * @returns The comma-joined region names (empty when there are none).
 */
export function formatShippingLocations(
  locations: ShippingZoneLocation[],
  continents: ShopContinent[],
): string {
  return locations
    .filter((l) => l.type !== ALL_REGIONS)
    .map((l) => getShippingRegionLabel(l, continents))
    .join(', ');
}

/**
 * Formats a pickup location's address into a single line, dropping empty parts.
 *
 * @param location - The pickup location.
 *
 * @returns The `street, city, postcode, country` line (empty when all blank).
 */
export function formatPickupAddress(location: ShopPickupLocation): string {
  const { address_1, city, postcode, country } = location.address;
  return [address_1, city, postcode, country].filter(Boolean).join(', ');
}

/**
 * Builds the WooCommerce code for a state: `"<country>:<state>"` (e.g. `"US:CA"`).
 *
 * @param countryCode - The country code.
 * @param stateCode - The state code.
 *
 * @returns The combined state location code.
 */
export function getStateLocationCode(countryCode: string, stateCode: string): string {
  return `${countryCode}:${stateCode}`;
}

/**
 * Builds a continent location.
 *
 * @param code - The continent code.
 *
 * @returns The {@link ShippingZoneLocation}.
 */
export function getContinentLocation(code: string): ShippingZoneLocation {
  return { code, type: 'continent' };
}

/**
 * Builds a country location.
 *
 * @param code - The country code.
 *
 * @returns The {@link ShippingZoneLocation}.
 */
export function getCountryLocation(code: string): ShippingZoneLocation {
  return { code, type: 'country' };
}

/**
 * Builds a state location from its country and state codes.
 *
 * @param countryCode - The country code.
 * @param stateCode - The state code.
 *
 * @returns The {@link ShippingZoneLocation}.
 */
export function getStateLocation(
  countryCode: string,
  stateCode: string,
): ShippingZoneLocation {
  return { code: getStateLocationCode(countryCode, stateCode), type: 'state' };
}

/**
 * Stable identity key for a location (`"<type>:<code>"`), used for selection
 * membership checks.
 *
 * @param location - The location.
 *
 * @returns The identity key.
 */
export function getLocationKey(location: ShippingZoneLocation): string {
  return `${location.type}:${location.code}`;
}

/**
 * Whether a location is the synthetic "all regions" sentinel.
 *
 * @param location - The location.
 *
 * @returns `true` for the all-regions sentinel.
 */
export function isAllRegionsLocation(location: ShippingZoneLocation): boolean {
  return location.type === ALL_REGIONS;
}

/**
 * Drops the all-regions sentinel from a location list (the API persists an empty
 * list to mean "all regions").
 *
 * @param locations - The locations.
 *
 * @returns The locations without the sentinel.
 */
export function removeAllRegionsLocation(
  locations: ShippingZoneLocation[],
): ShippingZoneLocation[] {
  return locations.filter((location) => !isAllRegionsLocation(location));
}

/**
 * Ensures a location list is non-empty by falling back to the all-regions
 * sentinel (so the selector shows "Everywhere" when nothing specific is chosen).
 *
 * @param locations - The locations.
 *
 * @returns The locations, or `[ALL_REGIONS_LOCATION]` when empty.
 */
export function withAllRegionsFallback(
  locations: ShippingZoneLocation[],
): ShippingZoneLocation[] {
  return locations.length > 0 ? locations : [ALL_REGIONS_LOCATION];
}

/**
 * Filters the continent tree by a search query, keeping a continent when it (or
 * any of its countries/states) matches and trimming countries/states to the
 * matches (a matched continent or country keeps all of its descendants). An empty
 * query returns the tree unchanged.
 *
 * @param continents - The region tree.
 * @param query - The raw search query (normalized internally).
 *
 * @returns The filtered tree.
 */
export function filterContinentsByQuery(
  continents: ShopContinent[],
  query: string,
): ShopContinent[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return continents;
  }
  const result: ShopContinent[] = [];
  for (const continent of continents) {
    const continentMatch = continent.name.toLowerCase().includes(q);
    const countries: ShopContinent['countries'] = [];
    for (const country of continent.countries) {
      const countryMatch = country.name.toLowerCase().includes(q);
      const states = country.states.filter((s) => s.name.toLowerCase().includes(q));
      if (continentMatch || countryMatch) {
        countries.push(country);
      } else if (states.length > 0) {
        countries.push({ ...country, states });
      }
    }
    if (continentMatch) {
      result.push(continent);
    } else if (countries.length > 0) {
      result.push({ ...continent, countries });
    }
  }
  return result;
}
